import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import MarketingShell from "@/components/MarketingShell";
import { Button } from "@/components/ui/button";
import { ShieldCheck, HeartPulse, Gift, Building2, ArrowRight } from "lucide-react";

const FEATURE_ICONS = [HeartPulse, Gift, ShieldCheck];

export default function Landing() {
  const [content, setContent] = useState(null);
  useEffect(() => { api.get("/site-content").then(({ data }) => setContent(data)); }, []);
  const c = content?.landing;

  return (
    <MarketingShell content={content} current="home">
      {/* Hero section — full-width background image (desktop only), text overlaid on top */}
      <section className="relative overflow-hidden">
        {/* Full-bleed image — hidden on mobile. Only render once CMS content has loaded so we don't flash the fallback image. */}
        {content && (
          <div className="hidden md:block absolute inset-0 z-0">
            <img
              src={
                c?.hero_image_url
                  ? c.hero_image_url
                  : c?.hero_image_path
                    ? fileUrl(c.hero_image_path)
                    : "https://images.unsplash.com/photo-1758691462321-9b6c98c40f7e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHw0fHxoYXBweSUyMGRpdmVyc2UlMjBwYXRpZW50cyUyMGRvY3RvciUyMGNsaW5pY3xlbnwwfHx8fDE3ODE0NDI1Njl8MA&ixlib=rb-4.1.0&q=85"
              }
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/0" />
            <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-transparent to-white/30" />
          </div>
        )}

        {/* Text content layer */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-20 md:pt-28 md:pb-40 min-h-[60vh] md:min-h-[640px] flex items-center">
          <div className="max-w-2xl animate-fade-up">
            <span className="label-eyebrow text-mr-accent">{c?.hero_eyebrow || "For hospitals & clinics · UAE"}</span>
            <h1 className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-4 text-mr-primary">
              {c?.hero_title_part1}
              <span className="text-mr-accent">{c?.hero_title_accent}</span>
              {c?.hero_title_part2}
            </h1>
            <p className="text-mr-secondary text-lg mt-6 max-w-xl leading-relaxed">{c?.hero_subtitle}</p>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/clinics">
                <Button data-testid="landing-browse-clinics-btn" size="lg" className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)] px-7 h-12">
                  <Building2 className="h-4 w-4 mr-2" /> Browse clinics & hospitals
                </Button>
              </Link>
              <Link to="/register-clinic">
                <Button data-testid="landing-cta-register" size="lg" variant="ghost" className="rounded-full h-12 text-mr-primary bg-white/60 backdrop-blur hover:bg-white">
                  Register your clinic <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="grid md:grid-cols-3 gap-6">
          {(c?.features || []).map((f, i) => {
            const I = FEATURE_ICONS[i % FEATURE_ICONS.length];
            return (
              <div key={i} className="bg-white rounded-2xl border border-mr p-7 hover:-translate-y-1 transition-all">
                <I className="h-6 w-6 text-mr-accent" />
                <h3 className="font-display text-xl font-semibold mt-4">{f.title}</h3>
                <p className="text-mr-secondary mt-2 leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>
    </MarketingShell>
  );
}
